import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { AlertTriangle, Link2Off } from 'lucide-react';

/**
 * CpscCertLinkRecords — CHA-267 PR-3. The cert-links table, extracted VERBATIM from CpscCertLinks
 * (CHA-229) so Register (Ruling 4) and the Link surface render the SAME list — one implementation of
 * unlink + the engine-resolved honest status, never a fork (§8.2):
 *  - status via the REAL engine (getCpscStatusForStyles) — a link never fabricates "certified" (#60);
 *  - Unlink only for non-REMOVED links (#61);
 *  - errors surfaced, never swallowed (§8.3).
 * `onUnlinked` (optional) lets the host react (the Link surface remounts its create form so a stale
 * "linked" success row dies with the link). `styleFilter` narrows rows (Register's filter bar).
 */
const s = (v) => (v == null ? '' : String(v));

const STATUS_LABEL = {
  certified: 'Certified', awaiting: 'Awaiting certification', submitted: 'Submitted for validation',
  in_progress: 'Draft in progress', failed: 'Certification failed', exempt: 'Exempt',
  review: 'Review (may need a CPC)', na: 'No CPSC cert needed', none: 'No cert (needs cert)',
};
const statusTone = (st) => (st === 'certified' ? 'text-carbon-green-50' : st === 'none' ? 'text-carbon-red-60'
  : (st === 'awaiting' || st === 'submitted' || st === 'in_progress') ? 'text-carbon-orange-70' : 'text-carbon-gray-70');

export default function CpscCertLinkRecords({ userProfile, onUnlinked, styleFilter = '' } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';
  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId, ...extra } : { ...extra }), [isPlatformRole, tenantId]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsError, setRecordsError] = useState(null);
  const [statusByStyle, setStatusByStyle] = useState({});

  useEffect(() => {
    if (!isAdmin) return undefined;
    let unsub;
    try {
      const db = getFirestore();
      unsub = onSnapshot(collection(db, 'cpsc_cert_links', tenantId, 'links'), (snap) => {
        setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setRecordsError(null);
      }, (err) => {
        console.error('cpsc_cert_links read failed', err);
        setRecordsError('Could not load existing cert links (check console).');
      });
    } catch (err) { console.error('cpsc_cert_links subscribe failed', err); setRecordsError('Cert links unavailable (check console).'); }
    return () => { if (unsub) unsub(); };
  }, [isAdmin, tenantId]);

  const activeStyles = useMemo(
    () => records.filter((r) => r.status === 'ACTIVE').map((r) => s(r.styleNo)).filter(Boolean),
    [records],
  );
  const activeStylesKey = useMemo(() => activeStyles.slice().sort().join(','), [activeStyles]);
  useEffect(() => {
    if (!isAdmin || !activeStyles.length) { setStatusByStyle({}); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const res = await httpsCallable(getFunctions(), 'getCpscStatusForStyles', { timeout: 150000 })(tenantArg({ styleNos: activeStyles })); // CHA-382
        if (!cancelled) {
          const map = {};
          for (const [sty, v] of Object.entries(res.data?.statuses || {})) map[sty] = v?.status || null;
          setStatusByStyle(map);
        }
      } catch (err) { console.error('cert-link status resolve failed', err); if (!cancelled) setStatusByStyle({}); }
    })();
    return () => { cancelled = true; };
  }, [activeStylesKey, isAdmin, tenantArg]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlink = useCallback(async (styleNo) => {
    setBusy(true); setError(null);
    try {
      await httpsCallable(getFunctions(), 'saveCpscStyleCertLink')(tenantArg({ operation: 'remove', styleNo }));
      if (onUnlinked) onUnlinked(styleNo);
    } catch (e) { setError(e?.message || 'Could not unlink.'); } finally { setBusy(false); }
  }, [tenantArg, onUnlinked]);

  if (!isAdmin) return null;

  const filter = s(styleFilter).trim().toLowerCase();
  // Filter across everything the row DISPLAYS (Codex P2) — style + the linked cert identifiers; an operator
  // must be able to find a link by the report/PID the table itself shows.
  const visible = filter
    ? records.filter((r) => `${s(r.styleNo)} ${s(r.linkedReportNo)} ${s(r.linkedProductId)}`.toLowerCase().includes(filter))
    : records;

  return (
    <div className="font-plex text-carbon-ink bg-carbon-white border border-carbon-border p-3">
      <h4 className="font-plex-mono text-[10px] uppercase tracking-[0.12em] font-semibold text-carbon-gray-70 mb-2">Cert links ({visible.length}{filter ? ` of ${records.length}` : ''})</h4>
      {error && <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
      {recordsError && (
        <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 mb-2 flex items-center gap-1"><AlertTriangle size={12} /> {recordsError}</div>
      )}
      {visible.length === 0 ? (
        <p className="text-xs text-carbon-gray-50">{filter ? 'No cert links match the filter.' : 'No cert links yet.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-carbon-gray-10"><tr className="text-left font-plex-mono text-[10px] uppercase tracking-[0.12em] font-normal text-carbon-gray-50 border-b border-carbon-border">
              <th className="py-1 pr-2">Style</th><th className="py-1 pr-2">Linked cert</th><th className="py-1 pr-2">Resolved status</th><th className="py-1 pr-2">Link</th><th className="py-1 pr-2">Actions</th>
            </tr></thead>
            <tbody>
              {visible.map((r) => {
                const st = r.status === 'ACTIVE' ? statusByStyle[s(r.styleNo)] : null;
                return (
                  <tr key={r.id} className="border-b border-carbon-gray-10">
                    <td className="py-1 pr-2 font-plex-mono font-medium text-carbon-ink">{r.styleNo}</td>
                    <td className="py-1 pr-2 text-carbon-gray-70">
                      {r.linkedProductId ? <span>PID <span className="font-plex-mono font-medium text-carbon-ink">{r.linkedProductId}</span></span> : null}
                      {r.linkedProductId && r.linkedReportNo ? ' · ' : null}
                      {r.linkedReportNo ? <span>Report <span className="font-plex-mono font-medium text-carbon-ink">{r.linkedReportNo}</span></span> : null}
                      {!r.linkedProductId && !r.linkedReportNo ? '—' : null}
                    </td>
                    <td className="py-1 pr-2">
                      {r.status !== 'ACTIVE' ? <span className="text-carbon-gray-40">—</span>
                        : st ? <span className={`font-medium ${statusTone(st)}`}>{STATUS_LABEL[st] || st}</span>
                          : <span className="text-carbon-gray-40">resolving…</span>}
                    </td>
                    <td className="py-1 pr-2">{r.status === 'REMOVED' ? <span className="font-plex-mono text-carbon-red-60">REMOVED</span> : <span className="font-plex-mono text-carbon-green-50">ACTIVE</span>}</td>
                    <td className="py-1 pr-2">
                      {r.status !== 'REMOVED' && <button onClick={() => unlink(r.styleNo)} disabled={busy} className="flex items-center gap-0.5 text-carbon-gray-70 hover:text-carbon-red-60 disabled:opacity-50" aria-label={`unlink ${r.styleNo}`}><Link2Off size={12} /> Unlink</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
